import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Info, ShieldCheck } from "lucide-react";

const KEY = "cookie-consent-v2";
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182; // 6 mois (recommandation CNIL)

type Consent = { analytics: boolean; marketing: boolean; date: string };

declare global {
  interface Window { gtag?: (...args: unknown[]) => void }
}

const applyConsent = (c: Pick<Consent, "analytics" | "marketing">) => {
  const m = c.marketing ? "granted" : "denied";
  window.gtag?.("consent", "update", {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: m, ad_user_data: m, ad_personalization: m,
  });
};

export const openCookiePreferences = () => window.dispatchEvent(new Event("open-cookie-preferences"));

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let stored: Consent | null = null;
    try { stored = JSON.parse(localStorage.getItem(KEY) || "null"); } catch { /* ignore */ }
    const expired = !stored || Date.now() - new Date(stored.date).getTime() > MAX_AGE_MS;
    if (expired) {
      localStorage.removeItem(KEY);
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
    setAnalytics(stored!.analytics);
    setMarketing(stored!.marketing);
  }, []);

  useEffect(() => {
    const open = () => { setCustom(true); setShow(true); };
    window.addEventListener("open-cookie-preferences", open);
    return () => window.removeEventListener("open-cookie-preferences", open);
  }, []);

  const save = (a: boolean, m: boolean) => {
    const c: Consent = { analytics: a, marketing: m, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(c));
    localStorage.removeItem("cookie-consent");
    applyConsent(c);
    setAnalytics(a); setMarketing(m);
    setShow(false); setCustom(false);
  };

  if (!show) return null;

  return (
    <div role="dialog" aria-label="Préférences cookies" className="fixed bottom-0 left-0 right-0 z-[100] p-4 bg-background border-t border-border shadow-lg">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-2 text-primary">
          <ShieldCheck className="h-5 w-5" />
          <span className="font-playfair text-lg">Votre vie privée</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre accord, des cookies de mesure d'audience
          (Google Analytics) et publicitaires. Vous pouvez accepter, refuser ou personnaliser vos choix, et les modifier à tout moment
          depuis la page Mentions légales. Votre choix est conservé 6 mois.{" "}
          <a href="/mentions-legales" className="text-primary underline inline-flex items-center gap-1">
            <Info className="h-3 w-3" />En savoir plus
          </a>
        </p>

        {custom && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-4">
              <div><p className="text-sm font-medium">Cookies nécessaires</p><p className="text-xs text-muted-foreground">Connexion, réservation, sécurité. Toujours actifs.</p></div>
              <Switch checked disabled aria-label="Cookies nécessaires" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div><p className="text-sm font-medium">Mesure d'audience</p><p className="text-xs text-muted-foreground">Google Analytics, statistiques de fréquentation.</p></div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Mesure d'audience" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div><p className="text-sm font-medium">Publicité</p><p className="text-xs text-muted-foreground">Mesure et personnalisation publicitaire Google.</p></div>
              <Switch checked={marketing} onCheckedChange={setMarketing} aria-label="Publicité" />
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          {custom ? (
            <Button variant="outline" onClick={() => save(analytics, marketing)}>Enregistrer mes choix</Button>
          ) : (
            <Button variant="ghost" onClick={() => setCustom(true)}>Personnaliser</Button>
          )}
          <Button variant="outline" onClick={() => save(false, false)}>Tout refuser</Button>
          <Button variant="outline" onClick={() => save(true, true)}>Tout accepter</Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
